import { EditorContent, useEditor, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, Link2, List, ListOrdered, Strikethrough } from "lucide-react";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { isRichTextEmpty, toEditorHtml } from "@/lib/rich-text";
import { cn } from "@/lib/utils";

interface RichTextEditorProps {
  /** HTML (or legacy plain text, which is converted to paragraphs). */
  value: string;
  onChange?: (html: string) => void;
  /** Show the content without a toolbar or the ability to edit it. */
  readOnly?: boolean;
  disabled?: boolean;
  id?: string;
  ariaLabel?: string;
  className?: string;
}

const CONTENT_STYLES =
  "[&_.ProseMirror]:outline-none [&_.ProseMirror_p]:my-2 [&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ul]:pl-5 [&_.ProseMirror_ol]:pl-5 [&_.ProseMirror_a]:text-primary [&_.ProseMirror_a]:underline [&_.ProseMirror_p:first-child]:mt-0 [&_.ProseMirror_p:last-child]:mb-0";

export function RichTextEditor({
  value,
  onChange,
  readOnly = false,
  disabled = false,
  id,
  ariaLabel,
  className,
}: RichTextEditorProps) {
  const editable = !readOnly && !disabled;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        underline: false,
        link: { openOnClick: false, autolink: true },
      }),
    ],
    content: toEditorHtml(value),
    editable,
    editorProps: {
      attributes: {
        ...(id ? { id } : {}),
        ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
        role: "textbox",
        "aria-multiline": "true",
        class: "min-h-32 px-3 py-2 text-sm",
      },
    },
    onUpdate: ({ editor: current }) => {
      onChange?.(current.isEmpty ? "" : current.getHTML());
    },
  });

  // Follow outside changes (a saved template loading, a form reset) without
  // fighting the user's own typing: only replace when the content really differs.
  useEffect(() => {
    if (!editor) return;
    const incoming = toEditorHtml(value);
    const current = editor.getHTML();
    if (incoming === current) return;
    if (isRichTextEmpty(incoming) && isRichTextEmpty(current)) return;
    editor.commands.setContent(incoming, { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  const active = useEditorState({
    editor,
    selector: ({ editor: current }) => ({
      bold: current?.isActive("bold") ?? false,
      italic: current?.isActive("italic") ?? false,
      strike: current?.isActive("strike") ?? false,
      bulletList: current?.isActive("bulletList") ?? false,
      orderedList: current?.isActive("orderedList") ?? false,
      link: current?.isActive("link") ?? false,
    }),
  });

  function setLink() {
    if (!editor) return;
    const previous = editor.getAttributes("link").href;
    const url = window.prompt(
      "Link address (leave blank to remove the link)",
      typeof previous === "string" ? previous : "https://",
    );
    if (url === null) return;
    if (url.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  }

  const tools = editor
    ? [
        {
          label: "Bold",
          icon: Bold,
          isActive: active?.bold,
          run: () => editor.chain().focus().toggleBold().run(),
        },
        {
          label: "Italic",
          icon: Italic,
          isActive: active?.italic,
          run: () => editor.chain().focus().toggleItalic().run(),
        },
        {
          label: "Strikethrough",
          icon: Strikethrough,
          isActive: active?.strike,
          run: () => editor.chain().focus().toggleStrike().run(),
        },
        {
          label: "Bulleted list",
          icon: List,
          isActive: active?.bulletList,
          run: () => editor.chain().focus().toggleBulletList().run(),
        },
        {
          label: "Numbered list",
          icon: ListOrdered,
          isActive: active?.orderedList,
          run: () => editor.chain().focus().toggleOrderedList().run(),
        },
        { label: "Link", icon: Link2, isActive: active?.link, run: setLink },
      ]
    : [];

  return (
    <div
      className={cn(
        "rounded-md border border-input bg-transparent shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50",
        disabled && "opacity-50",
        readOnly && "bg-muted/30",
        CONTENT_STYLES,
        className,
      )}
    >
      {!readOnly && (
        <div className="flex gap-1 border-b border-input p-1">
          {tools.map((tool) => (
            <Button
              key={tool.label}
              type="button"
              variant={tool.isActive ? "secondary" : "ghost"}
              size="icon-sm"
              aria-label={tool.label}
              aria-pressed={tool.isActive}
              title={tool.label}
              disabled={!editable}
              onClick={tool.run}
            >
              <tool.icon />
            </Button>
          ))}
        </div>
      )}
      <EditorContent editor={editor} />
    </div>
  );
}
